/** Stable identifiers accepted by the video export surface. */
export type RenderPresetId = "landscape-16x9" | "social-4x5" | "vertical-9x16";

/** Immutable video characteristics controlled by the application. */
export interface RenderPreset {
  readonly id: RenderPresetId;
  readonly label: string;
  readonly description: string;
  readonly width: number;
  readonly height: number;
  readonly framesPerSecond: 30;
}

/** Complete allowlist of production video outputs. */
export const RENDER_PRESETS: readonly RenderPreset[] = [
  {
    id: "landscape-16x9",
    label: "Landscape",
    description: "YouTube, web, and traditional video",
    width: 1920,
    height: 1080,
    framesPerSecond: 30,
  },
  {
    id: "social-4x5",
    label: "Social portrait",
    description: "Instagram and Facebook feed",
    width: 1080,
    height: 1350,
    framesPerSecond: 30,
  },
  {
    id: "vertical-9x16",
    label: "Vertical",
    description: "Instagram Reels and Stories, TikTok, and YouTube Shorts",
    width: 1080,
    height: 1920,
    framesPerSecond: 30,
  },
] as const;

/**
 * Resolves one application-owned output preset.
 *
 * @param id - Candidate preset identifier.
 * @returns Matching immutable preset.
 * @throws RangeError when the identifier is unsupported.
 */
export function resolveRenderPreset(id: string): RenderPreset {
  const preset = RENDER_PRESETS.find((candidate) => candidate.id === id);
  if (preset === undefined) {
    throw new RangeError(`Unsupported render preset: ${id}`);
  }
  return preset;
}
